import { inject, Injectable, DOCUMENT } from '@angular/core';

import { TemplatePortal } from '@angular/cdk/portal';
import { ScreenReaderService } from '../../services/screen-reader.service';
import { FocusTrapperFactory } from '../focus/focus-trapper-factory';
import { OVERLAY_FACTORY } from './overlay-factory.service';

import type { OverlayRef } from '@angular/cdk/overlay';
import type { FocusTrapper } from '../focus/focus-trapper-factory';
import type { OverlayConfig } from './overlay-factory.service';
import type { OnDestroy } from '@angular/core';

class OverlayRefContainer {
  private focusTrapper: FocusTrapper = undefined;

  constructor(private readonly overlayRef: OverlayRef,
              private readonly focusTrapperFactory: FocusTrapperFactory) {
    this.focusTrapper = this.focusTrapperFactory.create(
      this.overlayRef.overlayElement, { autoFocus: true, hideModalFromScreenReader: true });
    this.focusTrapper.savePreviouslyFocusedElement();
    this.focusTrapper.autoFocusFirstTabbableElement();
  }

  destroy(): void {
    this.focusTrapper.returnFocusAfterClose();
    this.focusTrapper.destroy();
    this.overlayRef.dispose();
  }
}

@Injectable({
  providedIn: 'root'
})
export class OverlayService implements OnDestroy {

  private overlayRefContainer: OverlayRefContainer;
  private ariaHiddenElements: Map<Element, string> = new Map<Element, string>();

  private readonly focusTrapperFactory = inject(FocusTrapperFactory);
  private readonly overlayFactory = inject(OVERLAY_FACTORY);
  private readonly document = inject(DOCUMENT);

  open(overlayConfig: OverlayConfig): OverlayRef {
    this.close();
    const overlayRef = this.overlayFactory.create(overlayConfig);
    const portal = new TemplatePortal(
      overlayConfig.overlayTemplate,
      overlayConfig.viewContainerRef,
      { $implicit: overlayConfig.overlayData });
    overlayRef.attach(portal);
    overlayRef.backdropClick().subscribe((): void => this.close());
    this.overlayRefContainer = new OverlayRefContainer(overlayRef, this.focusTrapperFactory);
    this.toggleNonOverlayContentScreenReaderVisibility(false);
    return overlayRef;
  }

  close(): void {
    if (this.isOpen()) {
      this.toggleNonOverlayContentScreenReaderVisibility(true);
      this.overlayRefContainer.destroy();
      this.overlayRefContainer = undefined;
    }
  }

  isOpen(): boolean {
    return this.overlayRefContainer !== undefined;
  }

  ngOnDestroy(): void {
    this.close();
  }

  private toggleNonOverlayContentScreenReaderVisibility(visible: boolean): void {
    if (visible) {
      ScreenReaderService.contentVisible(this.ariaHiddenElements);
    } else {
      const overlayContainer = this.document.getElementsByClassName('cdk-overlay-container');
      if (overlayContainer !== undefined && overlayContainer !== null && overlayContainer.length > 0) {
        ScreenReaderService.contentHidden(overlayContainer[0] as HTMLElement, this.ariaHiddenElements);
      }
    }
  }
}
